import React, { useEffect, useState } from "react";
import { Button, Box, Typography } from "@mui/material";
import RadioGroup from "../../../components/FormikFields/RadioGroup";
import { Formik, Form } from "formik";
import { useSelector } from "react-redux";
import Modal from "../../../components/Modal/Modal";
import Skeleton from "../../../components/Skeleton/Skeleton";
import { useAdminSettings } from "../../../hooks/adminSettings";
import { useSnackbarNotifications } from "../../../hooks/snackbar";
import { useTranslation } from "react-i18next";
import useScopedTranslation from "../../../hooks/useScopedTranslation";
import { formatDate } from "../../../utils/datetime";

const GlobalAssignmentSettingsForm = ({
  submitForm,
  values,
  untouched,
  setFieldTouched,
  errors,
  disabled,
  isLoading,
  isUpdating,
  allowGlobalAssignmentSettings,
  assignmentsForNewHiresNote
}) => {
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const t = useScopedTranslation("globalAssignmentSettings");
  const { t: globalT } = useTranslation();

  const handleCloseModal = () => setConfirmationModalOpen(false);
  const handleSubmitForm = () => {
    submitForm();
    handleCloseModal();
  };

  return (
    <Form>
      <Modal
        open={confirmationModalOpen}
        dialogContent={
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t("assignmentsForNewHires.confirmationText")}
          </Typography>
        }
        showCloseIconWithoutTitle={true}
        onClose={handleCloseModal}
        dialogActions={
          <>
            <Button
              color="primary"
              disabled={isUpdating}
              onClick={handleCloseModal}
            >
              {globalT("cancelBtn")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitForm}
              disabled={isUpdating}
            >
              {globalT("saveBtn")}
            </Button>
          </>
        }
      />
      <Box display="flex" flexDirection="column" mb={1}>
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "flex-start" }}
        >
          {allowGlobalAssignmentSettings && (
            <Box mx={4}>
              <Skeleton loading={isLoading && !isUpdating} height={54}>
                <RadioGroup
                  name="assignmentsForNewHires"
                  label={t("assignmentsForNewHires.label")}
                  radioButtons={[
                    {
                      label: t("assignmentsForNewHires.createdDate"),
                      value: "created_date"
                    },
                    {
                      label: t("assignmentsForNewHires.hireDate"),
                      value: "hire_date"
                    }
                  ]}
                  onClick={() =>
                    setFieldTouched("assignmentsForNewHires", true)
                  }
                />
                {assignmentsForNewHiresNote && (
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: "italic", ml: 3 }}
                  >
                    {assignmentsForNewHiresNote}
                  </Typography>
                )}
              </Skeleton>
            </Box>
          )}
        </Box>
      </Box>
      <Box display="flex" justifyContent="flex-end" mt={4}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setConfirmationModalOpen(true)}
          disabled={untouched || isLoading || isUpdating}
        >
          {t("saveSettings")}
        </Button>
      </Box>
    </Form>
  );
};

const GlobalAssignmentSettings = () => {
  const t = useScopedTranslation("globalAssignmentSettings");

  const {
    isLoading,
    isSaving,
    getGlobalAssignment,
    updateGlobalAssignment
  } = useAdminSettings();

  const {
    displaySuccessMessage,
    displayErrorMessage
  } = useSnackbarNotifications();

  const [settingsData, setSettingsData] = useState(null);

  const allowGlobalAssignmentSettings = useSelector(
    state => state.userState.user.permissions.allowGlobalAssignmentSettings
  );

  useEffect(() => {
    const fetchRoles = async () => {
      const data = await getGlobalAssignment();
      setSettingsData(data);
    };
    fetchRoles();
  }, []);

  const onSubmit = async values => {
    if (!allowGlobalAssignmentSettings) delete values.assignmentsForNewHires;
    const response = await updateGlobalAssignment(values);
    if (response.errors || response.error) {
      displayErrorMessage(t("errorMsg"));
    } else {
      setSettingsData(response);
      displaySuccessMessage(t("successMsg"));
    }
  };

  return (
    <Formik
      initialValues={{
        assignmentsForNewHires: settingsData
          ? settingsData.assignmentsForNewHires || "created_date"
          : false
      }}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {({ submitForm, values, errors, touched, setFieldTouched }) => (
        <GlobalAssignmentSettingsForm
          submitForm={submitForm}
          values={values}
          untouched={!touched || Object.keys(touched).length === 0}
          setFieldTouched={setFieldTouched}
          errors={errors}
          disabled={isSaving || isLoading}
          isLoading={isLoading}
          isUpdating={isSaving}
          allowGlobalAssignmentSettings={allowGlobalAssignmentSettings}
          assignmentsForNewHiresNote={
            settingsData?.assignmentsForNewHiresUpdatedBy
              ? t("assignmentsForNewHires.lastChange", {
                  userName: settingsData.assignmentsForNewHiresUpdatedBy,
                  date: formatDate(
                    settingsData.assignmentsForNewHiresUpdatedAt,
                    "MMMM DD, YYYY"
                  )
                })
              : ""
          }
        />
      )}
    </Formik>
  );
};

export default GlobalAssignmentSettings;
